import { z } from 'zod';
import { SymbolPrecisionsResponse } from '../services/infoService';
import { LimitPriceType, PriceReference } from '../types/preset';

// Define price type options for strict typing using the enum
export const PRICE_TYPE_VALUES = Object.values(LimitPriceType);
export type PriceTypeValues = LimitPriceType;

/**
 * Note on numeric field validation:
 * 
 * Numeric fields (amount, leverage, prices) accept both:
 * - Numbers (from direct user input in forms)
 * - Strings that can be converted to numbers (from webhook payloads)
 * 
 * This is to maintain compatibility with the backend WebhookPresetPayload schema,
 * which accepts these values as strings.
 */

// Helper function to create a schema that accepts both string and number
const numberOrStringNumber = (options: { min?: number; max?: number; errorMessage?: string; }) => {
  return z.union([
    z.string().refine(val => {
      const num = Number(val);
      return !isNaN(num) && 
             (options.min === undefined || num >= options.min) && 
             (options.max === undefined || num <= options.max);
    }, { message: options.errorMessage || "Must be a valid number" })
    .transform(val => Number(val)),
    z.number()
      .refine(val => 
        (options.min === undefined || val >= options.min) && 
        (options.max === undefined || val <= options.max),
        { message: options.errorMessage || "Value out of range" }
      )
  ]);
};

// Reuse the enums from presetSchemas
const OrderTypeEnum = z.enum(["market", "limit", "stop_market", "stop_limit", "layered", "cancel_all", "cancel_bids", "cancel_asks", "unknown"]);
const OrderSideEnum = z.enum(["buy", "sell"]);
const AmountTypeEnum = z.enum(["percent", "base", "quote"]);
const MarginModeEnum = z.enum(["cross", "isolated"]);
const PriceTypeEnum = z.nativeEnum(LimitPriceType);
const PriceReferenceEnum = z.nativeEnum(PriceReference);

// Schema for nested objects
const OrderFlagsSchema = z.object({
  reduce_only: z.boolean(),
  post_only: z.boolean(),
}).optional();

const TakeProfitSettingsSchema = z.object({
  enabled: z.boolean(),
  trigger_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Trigger price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  price_type: PriceTypeEnum.nullable().optional(),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().nullable().optional(),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  limit_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.nullable().optional(),
});

const StopLossSettingsSchema = z.object({
  enabled: z.boolean(),
  trigger_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Trigger price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  price_type: PriceTypeEnum.nullable().optional(),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().nullable().optional(),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  limit_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.nullable().optional(),
});

const LayeredOrderSettingsSchema = z.object({
  parts: numberOrStringNumber({ 
    min: 2, 
    max: 50, 
    errorMessage: "Must have at least 2 parts and maximum 50 parts allowed" 
  }).transform(val => Math.floor(Number(val))), // Ensure integer
  step: numberOrStringNumber({ 
    min: 0.000001, 
    errorMessage: "Step must be positive" 
  }),
}).nullable().optional();

// Base schema for order form
export const orderFormSchema = z.object({
  name: z.string().optional().default('ManualOrder'),
  description: z.string().nullable().optional(),
  symbol: z.string().min(1, { message: "Symbol is required" }),
  order_type: OrderTypeEnum,
  side: OrderSideEnum,
  amount: numberOrStringNumber({ 
    min: 0.000001, 
    errorMessage: "Amount must be a positive number" 
  }),
  amount_type: AmountTypeEnum,
  margin_mode: MarginModeEnum.optional().default('cross'),
  leverage: numberOrStringNumber({ 
    min: 1, 
    max: 50, 
    errorMessage: "Leverage must be between 1x and 50x" 
  }),
  limit_price: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.optional().default(LimitPriceType.ABSOLUTE),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? 0 : Number(val))
      .nullable()
      .optional()
      .default('0'),
    z.number().nullable().optional().default(0),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  stop_price: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Stop price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  take_profit: TakeProfitSettingsSchema.optional(),
  stop_loss: StopLossSettingsSchema.optional(),
  layered: LayeredOrderSettingsSchema,
  flags: OrderFlagsSchema.optional(),
  preset_type: z.literal('preset').optional().default('preset'),
});

// Export types for use with react-hook-form
export type OrderFormData = z.infer<typeof orderFormSchema>;

// Create a validation function that can be called with current form state
// to perform runtime validations that depend on form context
export const validateOrderForm = (
  data: OrderFormData, 
  symbolPrecisions: SymbolPrecisionsResponse = {},
  minOrderSizes: Record<string, number> = {}
): { 
  success: boolean; 
  errors: Record<string, string[]> 
} => {
  const errors: Record<string, string[]> = {};
  
  // Validate amount
  if (data.amount_type === 'percent' && data.amount > 100) {
    errors.amount = errors.amount || [];
    errors.amount.push("Percentage cannot exceed 100%");
  }
  
  // Validate minimum order size for base amount type
  if (data.amount_type === 'base' && minOrderSizes[data.symbol] && data.amount < minOrderSizes[data.symbol]) {
    errors.amount = errors.amount || [];
    errors.amount.push(`Minimum order size for ${data.symbol} is ${minOrderSizes[data.symbol]}`);
  }
  
  // Validate price precision for limit price
  if (data.limit_price && symbolPrecisions[data.symbol]?.price_decimals !== undefined) {
    const precision = symbolPrecisions[data.symbol].price_decimals;
    const step = parseFloat(`0.${'0'.repeat(precision-1)}1`);
    const remainder = data.limit_price % step;
    
    if (remainder > Number.EPSILON && Math.abs(remainder - step) > Number.EPSILON) {
      errors.limit_price = errors.limit_price || [];
      errors.limit_price.push(`Limit price must be a multiple of ${step}`);
    }
  }
  
  // Validate price precision for stop price
  if (data.stop_price && symbolPrecisions[data.symbol]?.price_decimals !== undefined) {
    const precision = symbolPrecisions[data.symbol].price_decimals;
    const step = parseFloat(`0.${'0'.repeat(precision-1)}1`);
    const remainder = data.stop_price % step;
    
    if (remainder > Number.EPSILON && Math.abs(remainder - step) > Number.EPSILON) {
      errors.stop_price = errors.stop_price || [];
      errors.stop_price.push(`Stop price must be a multiple of ${step}`);
    }
  }
  
  return {
    success: Object.keys(errors).length === 0,
    errors
  };
}; 